import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import { getERA } from '../services/insuranceService';
import Badge from '../components/common/Badge';
import Spinner from '../components/common/Spinner';

const STATUS_VARIANTS = {
  draft: 'default',
  submitted: 'info',
  pending: 'warning',
  paid: 'success',
  denied: 'error',
  partial: 'warning',
};

const fmt = (val) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val ?? 0);

export default function ERADetail() {
  const { id } = useParams();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['era', id],
    queryFn: () => getERA(id),
  });

  if (isLoading) return <div className="flex justify-center py-20"><Spinner /></div>;
  if (isError) return <div className="text-center py-20 text-danger-600 dark:text-danger-400">Failed to load ERA.</div>;

  const { era } = data;

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/insurance/eras" className="text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 transition-colors">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">{era.payerName}</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
            ERA — {format(parseISO(era.paymentDate), 'MMMM d, yyyy')}
          </p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="grid grid-cols-3 gap-6 text-sm">
          {[
            ['Payer ID', era.payerId],
            ['Check #', era.checkNumber],
            ['Payment date', format(parseISO(era.paymentDate), 'MMM d, yyyy')],
            ['Total paid', fmt(era.totalPaid)],
            ['# Claims', era.claims?.length],
          ].filter(([, v]) => v !== undefined && v !== null && v !== '').map(([label, value]) => (
            <div key={label}>
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">{label}</p>
              <p className="text-neutral-900 dark:text-neutral-100">{value}</p>
            </div>
          ))}
          {era.notes && (
            <div className="col-span-3">
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">Notes</p>
              <p className="text-neutral-700 dark:text-neutral-300">{era.notes}</p>
            </div>
          )}
        </div>
      </div>

      {era.claims && era.claims.length > 0 && (
        <div className="card p-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
            <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Claims ({era.claims.length})</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Client</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Invoice</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Billed</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Paid</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
              {era.claims.map((claim) => (
                <tr key={claim.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors">
                  <td className="px-6 py-4 text-neutral-700 dark:text-neutral-300">
                    {claim.client ? `${claim.client.firstName} ${claim.client.lastName}` : '—'}
                  </td>
                  <td className="px-6 py-4">
                    {claim.invoice ? (
                      <Link to={`/billing/${claim.invoice.id}`} className="text-primary-600 hover:underline">
                        {claim.invoice.invoiceNumber}
                      </Link>
                    ) : '—'}
                  </td>
                  <td className="px-6 py-4 text-right text-neutral-900 dark:text-neutral-100">{fmt(claim.billedAmount)}</td>
                  <td className="px-6 py-4 text-right text-green-600 dark:text-green-400 font-medium">{fmt(claim.paidAmount)}</td>
                  <td className="px-6 py-4">
                    <Badge label={claim.status} variant={STATUS_VARIANTS[claim.status] || 'default'} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
